import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApolloError, getApolloContext, gql } from "@apollo/client";
import { print } from "graphql";
import { getCustomDynamicHeaders } from "@src/lib/graphql/apollo";
import { useToast } from "@src/lib/hooks/useToast";
import {
  LevelMeter,
  createLevelMeter,
} from "@src/features/aiAssist/hooks/useDictation";
import {
  OpusAgentVoiceEndReason,
  OpusAgentVoiceTranscriptLineInput,
  OpusAgentVoiceTranscriptRole,
} from "@src/types.generated";
import type {
  OpusAgentTurnState,
  SendMessageOptions,
} from "../useOpusAgentChat";
import {
  EndOpusAgentVoiceSessionDocument,
  EndOpusAgentVoiceSessionMutation,
  EndOpusAgentVoiceSessionMutationVariables,
  StartOpusAgentVoiceSessionDocument,
  StartOpusAgentVoiceSessionMutation,
} from "./useOpusAgentVoice.generated";
import {
  ASK_OPUS_AGENT_TOOL,
  AskOutcome,
  ClientEvent,
  ResponseGate,
  EMPTY_USAGE,
  STOP_OPUS_AGENT_TOOL,
  VoiceUsageTotals,
  addUsage,
  askOutcome,
  createResponse,
  findAskTurn,
  functionCallOutput,
  latestActivityLabel,
  parseAskRequest,
  parseServerEvent,
  progressResponse,
  realtimeCallsUrl,
  sessionUpdate,
  systemNote,
} from "./realtime";

gql`
  mutation StartOpusAgentVoiceSession {
    startOpusAgentVoiceSession {
      success
      error {
        code
        message
      }
      voiceSession {
        id
        realtimeModel
      }
      clientSecret
      sessionConfig
      maxDurationSeconds
    }
  }

  mutation EndOpusAgentVoiceSession($input: EndOpusAgentVoiceSessionInput!) {
    endOpusAgentVoiceSession(input: $input) {
      success
    }
  }
`;

export type VoiceStatus =
  | "idle"
  | "connecting"
  | "listening"
  | "thinking"
  | "speaking";

// Hang up after this long with nobody talking and nothing in flight.
export const VOICE_IDLE_MS = 90 * 1000;
// Spoken progress at most this often while Opus Agent works.
export const PROGRESS_NOTE_MS = 8 * 1000;
// End the call this long before the server's limit, so a last ask is never
// refused mid-sentence (the server counts from when it minted the session).
export const TIME_LIMIT_MARGIN_MS = 10 * 1000;
// Give up waiting on one ask after this long; the answer still lands on screen.
export const ASK_TIMEOUT_MS = 5 * 60 * 1000;

type PendingAsk = {
  callId: string;
  request: string;
  preexistingIds: ReadonlySet<string>;
  startedAt: number;
  // The ask's turn has shown up in the chat at least once.
  seen: boolean;
  lastNoteAt: number;
  lastNoteLabel: string | null;
};

export type OpusAgentVoice = {
  status: VoiceStatus;
  isActive: boolean;
  muted: boolean;
  start: () => void;
  end: () => void;
  toggleMute: () => void;
  // Loudness 0–1 of the admin's mic and of the voice's reply.
  getInputLevel: () => number;
  getOutputLevel: () => number;
};

export const isVoiceSupported = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.RTCPeerConnection !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia;

// Voice mode: a live WebRTC call between the browser and OpenAI Realtime.
// The realtime model only listens and speaks; every question it gets goes to
// Opus Agent through the ask_opus_agent tool, which this hook runs as an
// ordinary send (tagged with the voice session) and answers from the turn's
// events once it settles. The chat hook stays the single owner of turns and
// polling; this hook only reads them.
export const useOpusAgentVoice = ({
  turns,
  sendMessage,
  abort,
}: {
  turns: OpusAgentTurnState[];
  sendMessage: (message: string, options?: SendMessageOptions) => void;
  abort: () => void;
}): OpusAgentVoice => {
  const { addErrorToast } = useToast();
  // The client from context rather than useMutation, which throws without a
  // provider: the drawer mounts this hook whether or not voice is enabled.
  const client = useContext(getApolloContext()).client;
  const clientRef = useRef(client);
  clientRef.current = client;
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [muted, setMuted] = useState(false);
  const statusRef = useRef<VoiceStatus>("idle");
  const setVoiceStatus = useCallback((next: VoiceStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const sessionIdRef = useRef<string | null>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const meterRef = useRef<LevelMeter | null>(null);
  const outputMeterRef = useRef<LevelMeter | null>(null);
  const pendingRef = useRef<PendingAsk | null>(null);
  const speakingRef = useRef(false);
  const transcriptRef = useRef<OpusAgentVoiceTranscriptLineInput[]>([]);
  const utterancesRef = useRef<string[]>([]);
  const untranscribedRef = useRef(0);
  const discardRef = useRef(0);
  const deferTimerRef = useRef<number | null>(null);
  const deferredAskRef = useRef<{ callId: string; request: string } | null>(
    null,
  );
  const usageRef = useRef<VoiceUsageTotals>(EMPTY_USAGE);
  const lastActivityRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  // Bumped per call: continuations of an ended call must not act.
  const generationRef = useRef(0);
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const abortRef = useRef(abort);
  abortRef.current = abort;

  const send = useCallback((event: object) => {
    const channel = channelRef.current;
    if (channel?.readyState === "open") channel.send(JSON.stringify(event));
  }, []);

  const gateRef = useRef<ResponseGate | null>(null);
  gateRef.current ??= new ResponseGate((event) => send(event));
  const requestResponse = useCallback(
    (event: ClientEvent) => gateRef.current?.request(event),
    [],
  );

  const settleStatus = useCallback(() => {
    if (statusRef.current === "idle" || statusRef.current === "connecting")
      return;
    if (speakingRef.current) setVoiceStatus("speaking");
    else if (pendingRef.current) setVoiceStatus("thinking");
    else setVoiceStatus("listening");
  }, [setVoiceStatus]);

  const teardown = useCallback(() => {
    generationRef.current += 1;
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current = [];
    channelRef.current?.close();
    channelRef.current = null;
    peerRef.current?.close();
    peerRef.current = null;
    micRef.current?.getTracks().forEach((track) => track.stop());
    micRef.current = null;
    meterRef.current?.close();
    meterRef.current = null;
    outputMeterRef.current?.close();
    outputMeterRef.current = null;
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current = null;
    }
    pendingRef.current = null;
    utterancesRef.current = [];
    untranscribedRef.current = 0;
    discardRef.current = 0;
    deferredAskRef.current = null;
    speakingRef.current = false;
    gateRef.current?.reset();
    setMuted(false);
    setVoiceStatus("idle");
  }, [setVoiceStatus]);

  const endSessionOnServer = useCallback(
    (input: EndOpusAgentVoiceSessionMutationVariables["input"]): void => {
      clientRef.current
        ?.mutate<
          EndOpusAgentVoiceSessionMutation,
          EndOpusAgentVoiceSessionMutationVariables
        >({ mutation: EndOpusAgentVoiceSessionDocument, variables: { input } })
        .catch(() => undefined);
    },
    [],
  );

  const endCall = useCallback(
    (reason: OpusAgentVoiceEndReason) => {
      const sessionId = sessionIdRef.current;
      const transcript = transcriptRef.current;
      const usage = usageRef.current;
      sessionIdRef.current = null;
      transcriptRef.current = [];
      usageRef.current = EMPTY_USAGE;
      teardown();
      if (!sessionId) return;
      endSessionOnServer({
        id: sessionId,
        endReason: reason,
        transcript,
        usage,
      });
    },
    [teardown, endSessionOnServer],
  );
  const endCallRef = useRef(endCall);
  endCallRef.current = endCall;

  const dropUtterances = () => {
    utterancesRef.current = [];
    discardRef.current = untranscribedRef.current;
  };

  const settleTranscription = (transcript: string | null) => {
    untranscribedRef.current = Math.max(0, untranscribedRef.current - 1);
    if (discardRef.current > 0) discardRef.current -= 1;
    else if (transcript?.trim()) utterancesRef.current.push(transcript.trim());
    if (untranscribedRef.current === 0) flushDeferredAskRef.current();
  };

  const recordLine = (role: OpusAgentVoiceTranscriptRole, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    transcriptRef.current.push({
      role,
      text: trimmed,
      at: new Date().toISOString(),
    });
  };

  const resolvePending = useCallback(
    (outcome: AskOutcome) => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      lastActivityRef.current = Date.now();
      send(functionCallOutput(pending.callId, outcome));
      requestResponse(createResponse());
      settleStatus();
    },
    [send, requestResponse, settleStatus],
  );
  const resolvePendingRef = useRef(resolvePending);
  resolvePendingRef.current = resolvePending;

  const dispatchAsk = useCallback(
    (callId: string, parsedRequest: string) => {
      const refuse = (error: string) => {
        send(functionCallOutput(callId, { error }));
        requestResponse(createResponse());
      };
      const request = utterancesRef.current.join(" ") || parsedRequest;
      utterancesRef.current = [];
      const sessionId = sessionIdRef.current;
      // Untagged, the send would run as a typed turn (with write tools).
      if (!sessionId) return refuse("Voice mode has ended.");
      const superseded = pendingRef.current;
      if (superseded) {
        // The new send supersedes the running turn server-side; close out
        // the old call without prompting a reply for it.
        send(functionCallOutput(superseded.callId, { status: "replaced" }));
      }
      pendingRef.current = {
        callId,
        request,
        preexistingIds: new Set(turnsRef.current.map((turn) => turn.id)),
        startedAt: Date.now(),
        seen: false,
        lastNoteAt: Date.now(),
        lastNoteLabel: null,
      };
      sendMessageRef.current(request, { voiceSessionId: sessionId });
      settleStatus();
    },
    [send, requestResponse, settleStatus],
  );

  const flushDeferredAsk = useCallback(() => {
    const deferred = deferredAskRef.current;
    if (!deferred) return;
    deferredAskRef.current = null;
    untranscribedRef.current = 0;
    discardRef.current = 0;
    dispatchAsk(deferred.callId, deferred.request);
  }, [dispatchAsk]);
  const flushDeferredAskRef = useRef(flushDeferredAsk);
  flushDeferredAskRef.current = flushDeferredAsk;

  const handleToolCall = useCallback(
    (callId: string, name: string, argumentsJson: string) => {
      lastActivityRef.current = Date.now();
      if (name === STOP_OPUS_AGENT_TOOL) {
        deferredAskRef.current = null;
        dropUtterances();
        const wasPending = pendingRef.current;
        if (wasPending) {
          send(functionCallOutput(wasPending.callId, { status: "stopped" }));
          pendingRef.current = null;
          // Only the voice ask: a turn the admin typed is theirs to stop.
          abortRef.current();
        }
        send(
          functionCallOutput(callId, {
            status: wasPending ? "stopped" : "nothing_running",
          }),
        );
        requestResponse(createResponse());
        settleStatus();
        return;
      }
      const refuse = (error: string) => {
        send(functionCallOutput(callId, { error }));
        requestResponse(createResponse());
      };
      if (name !== ASK_OPUS_AGENT_TOOL) return refuse(`Unknown tool ${name}`);
      const parsedRequest = parseAskRequest(argumentsJson);
      if (!parsedRequest) return refuse("Missing request.");
      if (untranscribedRef.current > 0) {
        const replaced = deferredAskRef.current;
        if (replaced) {
          send(functionCallOutput(replaced.callId, { status: "replaced" }));
        }
        if (deferTimerRef.current !== null) {
          window.clearTimeout(deferTimerRef.current);
        }
        deferredAskRef.current = { callId, request: parsedRequest };
        deferTimerRef.current = window.setTimeout(
          () => flushDeferredAskRef.current(),
          1500,
        );
        timersRef.current.push(deferTimerRef.current);
        return;
      }
      dispatchAsk(callId, parsedRequest);
    },
    [send, requestResponse, settleStatus, dispatchAsk],
  );

  const handleServerEvent = useCallback(
    (raw: string) => {
      const event = parseServerEvent(raw);
      switch (event.type) {
        case "input_audio_buffer.speech_started":
          lastActivityRef.current = Date.now();
          break;
        case "input_audio_buffer.speech_stopped":
          untranscribedRef.current += 1;
          break;
        case "output_audio_buffer.started":
          speakingRef.current = true;
          lastActivityRef.current = Date.now();
          settleStatus();
          break;
        case "output_audio_buffer.stopped":
        case "output_audio_buffer.cleared":
          speakingRef.current = false;
          lastActivityRef.current = Date.now();
          settleStatus();
          break;
        case "response.function_call_arguments.done":
          handleToolCall(event.call_id, event.name, event.arguments);
          break;
        case "conversation.item.input_audio_transcription.completed":
          recordLine(OpusAgentVoiceTranscriptRole.User, event.transcript);
          settleTranscription(event.transcript);
          break;
        case "conversation.item.input_audio_transcription.failed":
          settleTranscription(null);
          break;
        case "response.output_audio_transcript.done":
          recordLine(OpusAgentVoiceTranscriptRole.Assistant, event.transcript);
          break;
        case "response.created":
          gateRef.current?.created(
            event.response?.id,
            event.response?.metadata,
          );
          break;
        case "response.done":
          usageRef.current = addUsage(usageRef.current, event.response?.usage);
          gateRef.current?.done(event.response?.id);
          if (
            event.response?.status === "completed" &&
            !event.response.output?.some(
              (item) => item.type === "function_call",
            )
          ) {
            dropUtterances();
          }
          break;
        case "error":
          // Recoverable protocol errors; the call carries on. A create that
          // lost the race with a server-started response retries after it.
          gateRef.current?.error(event.error?.event_id, event.error?.code);
          console.warn("Opus Agent voice:", event.error?.message);
          break;
        default:
          break;
      }
    },
    [handleToolCall, settleStatus],
  );

  // Answer the pending ask once its turn settles; narrate progress meanwhile.
  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || statusRef.current === "idle") return;
    const turn = findAskTurn(turns, pending.request, pending.preexistingIds);
    if (turn) pending.seen = true;
    // Gone after it appeared: the admin switched chats, or edited it away.
    const outcome: AskOutcome | null =
      !turn && pending.seen ? { status: "stopped" } : askOutcome(turn);
    if (outcome) {
      resolvePending(outcome);
      return;
    }
    if (!turn || speakingRef.current || gateRef.current?.busy) return;
    const label = latestActivityLabel(turn.events);
    if (
      label &&
      label !== pending.lastNoteLabel &&
      Date.now() - pending.lastNoteAt >= PROGRESS_NOTE_MS
    ) {
      pending.lastNoteAt = Date.now();
      pending.lastNoteLabel = label;
      gateRef.current?.sendIfIdle(progressResponse(label));
    }
  }, [turns, send, resolvePending]);

  const start = useCallback(async () => {
    if (statusRef.current !== "idle") return;
    setVoiceStatus("connecting");
    const generation = ++generationRef.current;
    const stale = () => generationRef.current !== generation;
    let mic: MediaStream;
    try {
      mic = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      if (!stale()) teardown();
      addErrorToast({ message: "Allow microphone access to use voice mode." });
      return;
    }
    if (stale()) {
      mic.getTracks().forEach((track) => track.stop());
      return;
    }
    micRef.current = mic;
    meterRef.current = createLevelMeter(mic);
    try {
      const client = clientRef.current;
      if (!client) throw new Error("No GraphQL client");
      const requestedAt = Date.now();
      const { data } = await client.mutate<StartOpusAgentVoiceSessionMutation>({
        mutation: StartOpusAgentVoiceSessionDocument,
      });
      const result = data?.startOpusAgentVoiceSession;
      if (stale()) {
        // Hung up while connecting: close the session the server opened.
        const orphanId = result?.voiceSession?.id;
        if (orphanId)
          endSessionOnServer({
            id: orphanId,
            endReason: OpusAgentVoiceEndReason.User,
          });
        return;
      }
      if (
        !result?.success ||
        !result.clientSecret ||
        !result.voiceSession?.id
      ) {
        teardown();
        addErrorToast({
          message: result?.error?.message ?? "Voice mode couldn't start.",
        });
        return;
      }
      sessionIdRef.current = result.voiceSession.id;
      transcriptRef.current = [];
      usageRef.current = EMPTY_USAGE;

      const peer = new RTCPeerConnection();
      peerRef.current = peer;
      const audio = new Audio();
      audio.autoplay = true;
      audioRef.current = audio;
      peer.ontrack = (trackEvent) => {
        const remote = trackEvent.streams[0] ?? null;
        audio.srcObject = remote;
        if (remote && !stale()) {
          outputMeterRef.current?.close();
          outputMeterRef.current = createLevelMeter(remote);
        }
      };
      mic.getTracks().forEach((track) => peer.addTrack(track, mic));
      const channel = peer.createDataChannel("oai-events");
      channelRef.current = channel;
      channel.onmessage = (message) => {
        if (!stale()) handleServerEvent(String(message.data));
      };
      channel.onopen = () => {
        if (stale()) return;
        lastActivityRef.current = Date.now();
        setVoiceStatus("listening");
        // The /calls exchange starts a default session: apply the server's
        // persona and tools before the first response.
        send(sessionUpdate(result.sessionConfig));
        // A note, not response instructions: those would replace the persona
        // (and the admin's name) for the greeting.
        send(
          systemNote(
            "The call just started. Greet the admin by first name in a few words and ask what they need.",
          ),
        );
        requestResponse(createResponse({ noTools: true }));
      };
      peer.onconnectionstatechange = () => {
        if (stale()) return;
        if (peer.connectionState === "failed") {
          endCallRef.current(OpusAgentVoiceEndReason.Error);
          addErrorToast({ message: "Voice mode lost its connection." });
        }
      };

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      const response = await fetch(
        realtimeCallsUrl(result.voiceSession.realtimeModel),
        {
          method: "POST",
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${result.clientSecret}`,
            "Content-Type": "application/sdp",
          },
        },
      );
      if (stale()) return;
      if (!response.ok) throw new Error(`Realtime call ${response.status}`);
      await peer.setRemoteDescription({
        type: "answer",
        sdp: await response.text(),
      });
      if (stale()) return;

      const deadline =
        requestedAt +
        (result.maxDurationSeconds ?? 15 * 60) * 1000 -
        TIME_LIMIT_MARGIN_MS;
      timersRef.current.push(
        window.setTimeout(
          () => endCallRef.current(OpusAgentVoiceEndReason.TimeLimit),
          Math.max(0, deadline - Date.now()),
        ),
      );
      const checkIdle = () => {
        if (stale()) return;
        const pending = pendingRef.current;
        if (pending && Date.now() - pending.startedAt > ASK_TIMEOUT_MS) {
          resolvePendingRef.current({
            status: "failed",
            error:
              "Opus Agent is still working. Its answer will appear on screen.",
          });
        }
        const idle =
          !pendingRef.current &&
          !speakingRef.current &&
          Date.now() - lastActivityRef.current > VOICE_IDLE_MS;
        if (idle) {
          endCallRef.current(OpusAgentVoiceEndReason.Idle);
          return;
        }
        timersRef.current.push(window.setTimeout(checkIdle, 5000));
      };
      timersRef.current.push(window.setTimeout(checkIdle, 5000));
    } catch (error) {
      if (stale()) return;
      endCallRef.current(OpusAgentVoiceEndReason.Error);
      // A refusal (daily voice time used, voice not enabled) says why.
      const refusal =
        error instanceof ApolloError
          ? error.graphQLErrors[0]?.message
          : undefined;
      addErrorToast({
        message: refusal ?? "Voice mode couldn't connect. Try again.",
      });
    }
  }, [
    setVoiceStatus,
    teardown,
    addErrorToast,
    handleServerEvent,
    send,
    requestResponse,
    endSessionOnServer,
  ]);

  const end = useCallback(
    () => endCallRef.current(OpusAgentVoiceEndReason.User),
    [],
  );

  const toggleMute = useCallback(() => {
    const mic = micRef.current;
    if (!mic) return;
    setMuted((previous) => {
      const next = !previous;
      mic.getAudioTracks().forEach((track) => {
        track.enabled = !next;
      });
      return next;
    });
  }, []);

  const getInputLevel = useCallback(() => meterRef.current?.read() ?? 0, []);
  const getOutputLevel = useCallback(
    () => outputMeterRef.current?.read() ?? 0,
    [],
  );

  // Leaving the drawer (or the page's React tree) hangs up.
  useEffect(() => () => endCallRef.current(OpusAgentVoiceEndReason.User), []);

  // Closing or reloading the tab skips React cleanup: end the session with a
  // keepalive request so it is not charged its full length.
  useEffect(() => {
    const onPageHide = () => {
      const sessionId = sessionIdRef.current;
      const url = process.env.NEXT_PUBLIC_GRAPHQL_URL;
      if (!sessionId || !url) return;
      const variables: EndOpusAgentVoiceSessionMutationVariables = {
        input: {
          id: sessionId,
          endReason: OpusAgentVoiceEndReason.User,
          // keepalive bodies are capped at 64KB; the end matters more.
          transcript: transcriptRef.current.slice(-40),
          usage: usageRef.current,
        },
      };
      void fetch(url, {
        method: "POST",
        keepalive: true,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getCustomDynamicHeaders("EndOpusAgentVoiceSession"),
        },
        body: JSON.stringify({
          operationName: "EndOpusAgentVoiceSession",
          query: print(EndOpusAgentVoiceSessionDocument),
          variables,
        }),
      }).catch(() => undefined);
      sessionIdRef.current = null;
      // A page restored from the back/forward cache must not come back
      // holding a call whose session has ended.
      teardown();
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [teardown]);

  return {
    status,
    isActive: status !== "idle",
    muted,
    start: () => void start(),
    end,
    toggleMute,
    getInputLevel,
    getOutputLevel,
  };
};
